import React, { useCallback, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Input, Stack } from "@mui/material";
import { useDidUpdate, useRoleGuardHook } from "hooks";
import { usePickUserPermissions } from "store/selectors/user-selectors";
import { UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";
import { useContentContext } from "./content-context";

interface Props {
  id: number;
  title: string;
  onClose?: () => void;
}

export const TopHead: React.FC<Props> = ({ id, title, onClose }) => {
  const { updateDocument } = useContentContext();

  const [titleValue, setTitleValue] = useState<string>(title);
  const prevTitleRef = useRef<string>(title);

  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const titleInputEnabled = useRoleGuardHook(
    true,
    [RoleName.Admin, RoleName.Analyst],
    noAccessPermission || isViewerPermission
  );

  useDidUpdate(() => {
    if (title !== titleValue) {
      setTitleValue(title);
      prevTitleRef.current = title;
    }
  }, [title]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitleValue(e.target.value);
      updateDocument({ id, title: e.target.value });
    },
    [id, updateDocument]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === "Escape") {
      setTitleValue(prevTitleRef.current);
      setTimeout(() => (e.target as HTMLInputElement).blur());
    }
  }, []);

  return (
    <Stack
      sx={{
        p: "8px 16px 16px 16px",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%"
      }}
    >
      <Input
        value={titleValue}
        disabled={!titleInputEnabled}
        disableUnderline
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        sx={{
          fontSize: "2.125rem",
          fontWeight: 500,
          color: "#0A3142",
          flexGrow: 1,
          borderRadius: "4px",
          padding: "2px 6px",
          transition: "background-color 0.2s, box-shadow 0.2s",
          "&:hover:not(.Mui-disabled)": {
            backgroundColor: "#F6F8FC"
          },
          "&.Mui-focused": {
            backgroundColor: "#F6F8FC",
            boxShadow: "0 1px 0 0 #26A6AE"
          },
          "& input": { padding: 0 },
          "& input.Mui-disabled": {
            color: "#0A3142",
            WebkitTextFillColor: "#0A3142"
          }
        }}
        inputProps={{ dir: "auto" }}
      />
      {onClose && (
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      )}
    </Stack>
  );
};
